package diana.soleil.design.iraniancanadienbazzar.core.firebase

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.Blob
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.math.max
import kotlin.math.roundToInt

internal actual suspend fun downscaleImage(
    bytes: ByteArray,
    maxDimension: Int,
    quality: Int,
): CompressedImage? {
    return try {
        val int8 = bytes.unsafeCast<Int8Array>()
        val blob = Blob(arrayOf(Uint8Array(int8.buffer, int8.byteOffset, int8.length)))
        // createImageBitmap applies EXIF orientation ("from-image" is the browser default).
        val bitmap = window.createImageBitmap(blob).await()
        val longest = max(bitmap.width, bitmap.height)
        val scale = if (longest > maxDimension) maxDimension.toDouble() / longest else 1.0
        val targetWidth = (bitmap.width * scale).roundToInt().coerceAtLeast(1)
        val targetHeight = (bitmap.height * scale).roundToInt().coerceAtLeast(1)
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = targetWidth
        canvas.height = targetHeight
        val context = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return null
        context.drawImage(bitmap, 0.0, 0.0, targetWidth.toDouble(), targetHeight.toDouble())
        val jpegBlob = suspendCoroutine<Blob?> { continuation ->
            canvas.toBlob({ result -> continuation.resume(result) }, "image/jpeg", quality / 100.0)
        } ?: return null
        val jpegBytes = jpegBlob.readBytes()
        if (jpegBytes.isEmpty()) return null
        CompressedImage(jpegBytes, "image/jpeg")
    } catch (_: Throwable) {
        null
    }
}

private suspend fun Blob.readBytes(): ByteArray = suspendCoroutine { continuation ->
    val reader = FileReader()
    reader.onload = {
        val buffer = reader.result.unsafeCast<ArrayBuffer>()
        continuation.resume(Int8Array(buffer).unsafeCast<ByteArray>())
    }
    reader.onerror = { continuation.resume(ByteArray(0)) }
    reader.readAsArrayBuffer(this)
}
